import * as React from "react";
import {
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Checkbox,
  Box,
  Stack,
  Tabs,
  Tab,
  Collapse,
  FormControlLabel
} from "../../components/mui";
import {
  SearchRoundedIcon,
  RestartAltRoundedIcon,
  GetAppRoundedIcon,
  SummarizeRoundedIcon,
  ExpandMoreRoundedIcon,
  ExpandLessRoundedIcon,
  SwapVertRoundedIcon
} from "../../components/icons";

import "../../styles/main.css";
import "../../styles/pages/condoPayment.css";
import "../../styles/pages/condoUnits.css";
import "../../styles/pages/paymentSummary.css";
import PageLayout from "../../components/shared/PageLayout";
import PageHeader from "../../components/shared/PageHeader";
import DataTableCard from "../../components/shared/DataTableCard";
import MonthlySummaryTable from "./components/MonthlySummaryTable";
import {
  TABS,
  RENTAL_FILTERS,
  SECURITY_DEPOSIT_FILTERS,
  RENTAL_COLUMN_OPTIONS,
  MISC_COLUMN_OPTIONS,
  RENTAL_SUMMARY_ROWS,
  MISC_SUMMARY_ROWS,
  UTILITIES_SUMMARY_ROWS,
  ALLOWANCE_SUMMARY_ROWS,
  SECURITY_DEPOSIT_SUMMARY_ROWS
} from "./data/paymentSummaryData";

const RENTAL_MONTH_KEYS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december"
];

const RENTAL_COLUMNS = [
  { id: "unit", label: "Unit" },
  { id: "condo", label: "Condo" },
  { id: "lessee", label: "Lessee" },
  { id: "lessor", label: "Lessor" },
  { id: "paymentTerm", label: "Payment Term" },
  {
    id: "monthlyRent",
    label: "Monthly Rent",
    className: "payment-summary-number-cell"
  }
];

const RENTAL_ANNUAL_HEADERS = [
  { id: "annualPaid", label: "Annual Paid" },
  { id: "annualUnpaid", label: "Annual Unpaid" },
  { id: "annualTotal", label: "Annual Total" }
];

const MISC_COLUMNS = [
  { id: "unit", label: "Unit" },
  { id: "condo", label: "Condo" },
  { id: "lessee", label: "Lessee" },
  { id: "lessor", label: "Lessor" }
];

const MISC_ANNUAL_HEADERS = [
  { id: "annualTotal", label: "Annual Total" }
];

const UTILITIES_COLUMNS = [
  { id: "unit", label: "Unit" },
  { id: "condo", label: "Condo" },
  { id: "lessee", label: "Lessee" },
  { id: "lessor", label: "Lessor" }
];

const UTILITIES_ANNUAL_HEADERS = [
  { id: "annualTotal", label: "Annual Total" }
];

const ALLOWANCE_COLUMNS = [
  { id: "unit", label: "Unit" },
  { id: "condo", label: "Condo" },
  { id: "lessee", label: "Lessee" },
  { id: "lessor", label: "Lessor" }
];

const ALLOWANCE_ANNUAL_HEADERS = [
  { id: "annualTotal", label: "Annual Total" }
];

function RentalFiltersRow() {
  return (
    <Box className="condo-payment-filters-row payment-summary-filters-row-rental">
      <FormControl size="small" className="condo-payment-filter">
        <InputLabel>Condo</InputLabel>
        <Select defaultValue="all" label="Condo">
          {RENTAL_FILTERS.condo.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" className="condo-payment-filter">
        <InputLabel>Condo Unit</InputLabel>
        <Select defaultValue="all" label="Condo Unit">
          {RENTAL_FILTERS.condoUnit.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" className="condo-payment-filter">
        <InputLabel>Lessee</InputLabel>
        <Select defaultValue="all" label="Lessee">
          {RENTAL_FILTERS.lessee.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" className="condo-payment-filter">
        <InputLabel>Lessor</InputLabel>
        <Select defaultValue="all" label="Lessor">
          {RENTAL_FILTERS.lessor.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" className="condo-payment-filter">
        <InputLabel>Currency</InputLabel>
        <Select defaultValue="php" label="Currency">
          {RENTAL_FILTERS.currency.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" className="condo-payment-filter">
        <InputLabel>Year</InputLabel>
        <Select defaultValue="2025" label="Year">
          {RENTAL_FILTERS.year.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

function RentalSummaryTable({ showMonthlyPayments }) {
  const renderMonthlyCell = (row, key) => {
    const value = row[key];
    const isPositive = key === "january" || key === "february";

    const className = isPositive
      ? "payment-summary-number-cell payment-summary-text-positive"
      : "payment-summary-number-cell payment-summary-text-negative";

    return <span className={className}>{value}</span>;
  };

  const renderAnnualCells = (row) => [
    <TableCell
      key="annualPaid"
      className="payment-summary-number-cell payment-summary-text-positive payment-summary-text-strong"
    >
      {row.annualPaid}
    </TableCell>,
    <TableCell
      key="annualUnpaid"
      className="payment-summary-number-cell payment-summary-text-negative payment-summary-text-strong"
    >
      {row.annualUnpaid}
    </TableCell>,
    <TableCell
      key="annualTotal"
      className="payment-summary-number-cell payment-summary-text-strong"
    >
      {row.annualTotal}
    </TableCell>
  ];

  const renderTotalsRow = () => (
    <TableRow className="payment-summary-totals-row">
      {/* Empty cells align under non-month columns (Unit, Condo, Lessee, Lessor, Payment Term, Monthly Rent) */}
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />

      {/* Per-month totals (January to December) - only rendered when monthly columns are visible */}
      {showMonthlyPayments &&
        [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December"
        ].map((month) => (
          <TableCell key={month} className="payment-summary-totals-month-cell">
            <div className="payment-summary-totals-month-label">{month}</div>
            <div>
              <span className="payment-summary-totals-text-label">Paid</span>{" "}
              <span className="payment-summary-totals-text-paid">PHP 0.00</span>
            </div>
            <div>
              <span className="payment-summary-totals-text-label">Unpaid</span>{" "}
              <span className="payment-summary-totals-text-unpaid">PHP 0.00</span>
            </div>
            <div>
              <span className="payment-summary-totals-text-label">Total</span>{" "}
              <span className="payment-summary-totals-text-total">PHP 0.00</span>
            </div>
          </TableCell>
        ))}

      {/* Overall Paid / Unpaid / Grand Total - three separate columns (Rental only) */}
      <TableCell className="payment-summary-number-cell payment-summary-totals-grand-cell">
        <span className="payment-summary-totals-text-label">Paid</span>{" "}
        <span className="payment-summary-totals-text-paid">PHP 0.00</span>
      </TableCell>
      <TableCell className="payment-summary-number-cell payment-summary-totals-grand-cell">
        <span className="payment-summary-totals-text-label">Unpaid</span>{" "}
        <span className="payment-summary-totals-text-unpaid">PHP 0.00</span>
      </TableCell>
      <TableCell className="payment-summary-number-cell payment-summary-totals-grand-cell">
        <span className="payment-summary-totals-text-label">Grand Total</span>{" "}
        <span className="payment-summary-totals-text-total">PHP 0.00</span>
      </TableCell>
    </TableRow>
  );

  return (
    <MonthlySummaryTable
      columns={RENTAL_COLUMNS}
      rows={RENTAL_SUMMARY_ROWS}
      showMonthlyPayments={showMonthlyPayments}
      monthKeys={RENTAL_MONTH_KEYS}
      renderMonthlyCell={renderMonthlyCell}
      annualHeaders={RENTAL_ANNUAL_HEADERS}
      annualCellsRenderer={renderAnnualCells}
      renderTotalsRow={renderTotalsRow}
    />
  );
}

function AllowanceSummaryTable({ showMonthlyPayments }) {
  const renderAnnualCells = (row) => [
    <TableCell
      key="annualTotal"
      className="payment-summary-number-cell payment-summary-text-positive payment-summary-text-strong"
    >
      {row.annualTotal}
    </TableCell>
  ];

  const renderTotalsRow = () => (
    <TableRow className="payment-summary-totals-row">
      {/* Empty cells under Unit / Condo / Lessee / Lessor */}
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />

      {/* Optional January–December totals, same pattern as Utilities */}
      {showMonthlyPayments &&
        [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December"
        ].map((month) => (
          <TableCell key={month} className="payment-summary-totals-month-cell">
            <div className="payment-summary-totals-month-label">{month}</div>
            <div>
              <span className="payment-summary-totals-text-label">Paid</span>{" "}
              <span className="payment-summary-totals-text-paid">PHP 0.00</span>
            </div>
          </TableCell>
        ))}

      {/* Overall Paid summary only */}
      <TableCell className="payment-summary-number-cell payment-summary-totals-grand-cell">
        <span className="payment-summary-totals-text-label">Paid</span>{" "}
        <span className="payment-summary-totals-text-paid">PHP 0.00</span>
      </TableCell>
    </TableRow>
  );

  return (
    <MonthlySummaryTable
      columns={ALLOWANCE_COLUMNS}
      rows={ALLOWANCE_SUMMARY_ROWS}
      showMonthlyPayments={showMonthlyPayments}
      monthKeys={RENTAL_MONTH_KEYS}
      annualHeaders={ALLOWANCE_ANNUAL_HEADERS}
      annualCellsRenderer={renderAnnualCells}
      renderTotalsRow={renderTotalsRow}
    />
  );
}

function MiscSummaryTable({ showMonthlyPayments }) {
  const renderAnnualCells = (row) => [
    <TableCell
      key="annualTotal"
      className="payment-summary-number-cell payment-summary-text-positive payment-summary-text-strong"
    >
      {row.annualTotal}
    </TableCell>
  ];

  const renderTotalsRow = () => (
    <TableRow className="payment-summary-totals-row">
      {/* Empty cells under Unit / Condo / Lessee / Lessor */}
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />

      {/* Optional January–December totals, same pattern as Rental */}
      {showMonthlyPayments &&
        [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December"
        ].map((month) => (
          <TableCell key={month} className="payment-summary-totals-month-cell">
            <div className="payment-summary-totals-month-label">{month}</div>
            <div>
              <span className="payment-summary-totals-text-label">Paid</span>{" "}
              <span className="payment-summary-totals-text-paid">PHP 0.00</span>
            </div>
          </TableCell>
        ))}

      {/* Overall Paid summary only */}
      <TableCell className="payment-summary-number-cell payment-summary-totals-grand-cell">
        <span className="payment-summary-totals-text-label">Paid</span>{" "}
        <span className="payment-summary-totals-text-paid">PHP 0.00</span>
      </TableCell>
    </TableRow>
  );

  return (
    <MonthlySummaryTable
      columns={MISC_COLUMNS}
      rows={MISC_SUMMARY_ROWS}
      showMonthlyPayments={showMonthlyPayments}
      monthKeys={RENTAL_MONTH_KEYS}
      annualHeaders={MISC_ANNUAL_HEADERS}
      annualCellsRenderer={renderAnnualCells}
      renderTotalsRow={renderTotalsRow}
    />
  );
}

function UtilitiesSummaryTable({ showMonthlyPayments }) {
  const renderAnnualCells = (row) => [
    <TableCell
      key="annualTotal"
      className="payment-summary-number-cell payment-summary-text-positive payment-summary-text-strong"
    >
      {row.annualTotal}
    </TableCell>
  ];

  const renderTotalsRow = () => (
    <TableRow className="payment-summary-totals-row">
      {/* Empty cells under Unit / Condo / Lessee / Lessor */}
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />
      <TableCell className="payment-summary-totals-label-cell" />

      {/* Optional January–December totals, same pattern as Rental */}
      {showMonthlyPayments &&
        [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December"
        ].map((month) => (
          <TableCell key={month} className="payment-summary-totals-month-cell">
            <div className="payment-summary-totals-month-label">{month}</div>
            <div>
              <span className="payment-summary-totals-text-label">Paid</span>{" "}
              <span className="payment-summary-totals-text-paid">PHP 0.00</span>
            </div>
          </TableCell>
        ))}

      {/* Overall Paid summary only */}
      <TableCell className="payment-summary-number-cell payment-summary-totals-grand-cell">
        <span className="payment-summary-totals-text-label">Paid</span>{" "}
        <span className="payment-summary-totals-text-paid">PHP 0.00</span>
      </TableCell>
    </TableRow>
  );

  return (
    <MonthlySummaryTable
      columns={UTILITIES_COLUMNS}
      rows={UTILITIES_SUMMARY_ROWS}
      showMonthlyPayments={showMonthlyPayments}
      monthKeys={RENTAL_MONTH_KEYS}
      annualHeaders={UTILITIES_ANNUAL_HEADERS}
      annualCellsRenderer={renderAnnualCells}
      renderTotalsRow={renderTotalsRow}
    />
  );
}

function SecurityDepositSummaryTable() {
  return (
    <>
      <TableHead>
        <TableRow>
          <TableCell>Condo</TableCell>
          <TableCell>Condo Unit</TableCell>
          <TableCell>Lessee</TableCell>
          <TableCell>Lessor</TableCell>
          <TableCell>Contact</TableCell>
          <TableCell>Contract Start Date</TableCell>
          <TableCell>Contract End Date</TableCell>
          <TableCell>Termination Date</TableCell>
          <TableCell className="payment-summary-number-cell">Security Deposit</TableCell>
          <TableCell className="payment-summary-number-cell">Returned Amount</TableCell>
          <TableCell className="payment-summary-number-cell">Expense</TableCell>
          <TableCell>Skipped</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {SECURITY_DEPOSIT_SUMMARY_ROWS.map((row, index) => (
          <TableRow hover key={row.id}>
            <TableCell>{row.condo}</TableCell>
            <TableCell>{row.condoUnit}</TableCell>
            <TableCell>{row.lessee}</TableCell>
            <TableCell>{row.lessor}</TableCell>
            <TableCell>{row.contact}</TableCell>
            <TableCell>{row.contractStartDate}</TableCell>
            <TableCell>{row.contractEndDate}</TableCell>
            <TableCell>{row.terminationDate}</TableCell>
            <TableCell
              className={
                "payment-summary-number-cell" +
                (index === 0 ? " payment-summary-text-negative" : "")
              }
            >
              {row.securityDeposit}
            </TableCell>
            <TableCell
              className={
                "payment-summary-number-cell" +
                (index === 0 ? " payment-summary-text-negative" : "")
              }
            >
              {row.returnedAmount}
            </TableCell>
            <TableCell
              className={
                "payment-summary-number-cell" +
                (index === 0 ? " payment-summary-text-negative" : "")
              }
            >
              {row.expense}
            </TableCell>
            <TableCell>
              <Checkbox color="primary" checked={row.skipped} disabled />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </>
  );
}

function SecurityDepositFiltersRow() {
  return (
    <Box className="condo-payment-filters-row payment-summary-filters-row-rental">
      <FormControl size="small" className="condo-payment-filter">
        <InputLabel>Condo</InputLabel>
        <Select defaultValue="all" label="Condo">
          {SECURITY_DEPOSIT_FILTERS.condo.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" className="condo-payment-filter">
        <InputLabel>Condo Unit</InputLabel>
        <Select defaultValue="all" label="Condo Unit">
          {SECURITY_DEPOSIT_FILTERS.condoUnit.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" className="condo-payment-filter">
        <InputLabel>Security Deposit Returned</InputLabel>
        <Select defaultValue="all" label="Security Deposit Returned">
          {SECURITY_DEPOSIT_FILTERS.securityDepositReturned.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

export default function PaymentSummary({ onLogout, onNavigate }) {
  const [activeTab, setActiveTab] = React.useState("rental");
  const [columnSelectionOpen, setColumnSelectionOpen] = React.useState(true);
  const [showMonthlyPayments, setShowMonthlyPayments] = React.useState(false);

  return (
    <PageLayout
      activeKey="payment-summary"
      onLogout={onLogout}
      onNavigate={onNavigate}
      className="page-content"
    >
      <PageHeader
        icon={SummarizeRoundedIcon}
        title="Condo Unit Payment Summary"
        summary="View high-level payment status across all condo units."
      />

      {/* Tabs */}
      <Box className="tabs-container">
        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
            />
          ))}
        </Tabs>
      </Box>

      {/* Filters + actions */}
      <Box className="content-box condo-payment-filters">
        {(activeTab === "rental" || activeTab === "misc-expenses" || activeTab === "utilities" || activeTab === "allowance") && (
          <RentalFiltersRow />
        )}
        {activeTab === "security-deposit" && <SecurityDepositFiltersRow />}

        {(activeTab === "rental" || activeTab === "misc-expenses" || activeTab === "utilities" || activeTab === "security-deposit" || activeTab === "allowance") && (
          <Box className="condo-payment-toolbar condo-payment-toolbar--filters">
            <Button variant="contained" startIcon={<SearchRoundedIcon />}>
              Search
            </Button>
            <Button variant="outlined" startIcon={<RestartAltRoundedIcon />}>
              Reset filters
            </Button>
            <Button variant="outlined" startIcon={<GetAppRoundedIcon />}>
              Export
            </Button>
          </Box>
        )}

        {/* Column selection (reuses condo-units patterns) - not used for Security Deposit */}
        {(activeTab === "rental" || activeTab === "misc-expenses" || activeTab === "utilities" || activeTab === "allowance") && (
          <Box mt={1}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Button
                variant="text"
                className="condo-units-toggle-columns"
                onClick={() => setColumnSelectionOpen((prev) => !prev)}
                endIcon={columnSelectionOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
              >
                Column selection
              </Button>
            </Stack>

            <Collapse in={columnSelectionOpen} className="condo-units-columns">
              <Box className="condo-units-columns-grid">
                {(activeTab === "rental" ? RENTAL_COLUMN_OPTIONS : MISC_COLUMN_OPTIONS).map(({ id, label, defaultChecked }) => (
                  <FormControlLabel
                    key={id}
                    control={<Checkbox defaultChecked={defaultChecked} color="primary" />}
                    label={label}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>

      {/* Data table: Rental, Misc, Utilities, Allowance */}
      {(activeTab === "rental" || activeTab === "misc-expenses" || activeTab === "utilities" || activeTab === "allowance") && (
        <DataTableCard
          title={
            activeTab === "rental"
              ? "Rental Payment Summary"
              : activeTab === "misc-expenses"
              ? "Misc Expense Summary"
              : activeTab === "utilities"
              ? "Utilities Summary"
              : "Allowance Summary"
          }
          showExport={false}
          showEntriesControl={false}
          showFooter={false}
          actions={
            (activeTab === "rental" || activeTab === "misc-expenses" || activeTab === "utilities" || activeTab === "allowance") ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<SwapVertRoundedIcon />}
                onClick={() => setShowMonthlyPayments((prev) => !prev)}
              >
                {showMonthlyPayments ? "Hide Monthly Payments" : "Show Monthly Payments"}
              </Button>
            ) : null
          }
          totalEntries={
            activeTab === "rental"
              ? RENTAL_SUMMARY_ROWS.length
              : activeTab === "misc-expenses"
              ? MISC_SUMMARY_ROWS.length
              : activeTab === "utilities"
              ? UTILITIES_SUMMARY_ROWS.length
              : ALLOWANCE_SUMMARY_ROWS.length
          }
          startEntry={1}
          endEntry={
            activeTab === "rental"
              ? RENTAL_SUMMARY_ROWS.length
              : activeTab === "misc-expenses"
              ? MISC_SUMMARY_ROWS.length
              : activeTab === "utilities"
              ? UTILITIES_SUMMARY_ROWS.length
              : ALLOWANCE_SUMMARY_ROWS.length
          }
          currentPage={1}
          totalPages={1}
        >
          <TableContainer className="data-table-wrapper">
            <Table size="small">
              {activeTab === "rental" && (
                <RentalSummaryTable showMonthlyPayments={showMonthlyPayments} />
              )}
              {activeTab === "misc-expenses" && (
                <MiscSummaryTable showMonthlyPayments={showMonthlyPayments} />
              )}
              {activeTab === "utilities" && (
                <UtilitiesSummaryTable showMonthlyPayments={showMonthlyPayments} />
              )}
              {activeTab === "allowance" && (
                <AllowanceSummaryTable showMonthlyPayments={showMonthlyPayments} />
              )}
            </Table>
          </TableContainer>
        </DataTableCard>
      )}

      {/* Data table: Security Deposit (with entries + pagination, no monthly toggle) */}
      {activeTab === "security-deposit" && (
        <DataTableCard
          title="Security Deposit Summary"
          showExport={false}
          showEntriesControl
          showFooter
          totalEntries={SECURITY_DEPOSIT_SUMMARY_ROWS.length}
          startEntry={1}
          endEntry={SECURITY_DEPOSIT_SUMMARY_ROWS.length}
          currentPage={1}
          totalPages={1}
        >
          <TableContainer className="data-table-wrapper">
            <Table size="small">
              <SecurityDepositSummaryTable />
            </Table>
          </TableContainer>
        </DataTableCard>
      )}
    </PageLayout>
  );
}
